(function () {
  "use strict";

  // Loaded globally so the size/version switcher works everywhere the markup
  // can appear, including the quick view modal where the product section is
  // injected via innerHTML/append and inline <script> tags never execute.
  if (window.__sizeVersionSwitchInit) return;
  window.__sizeVersionSwitchInit = true;

  function findProductSection(root) {
    if (!root) return null;
    if (root.matches && root.matches("[data-product-section]")) return root;
    return root.querySelector("[data-product-section]");
  }

  function parseProductSectionFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return findProductSection(doc);
  }

  function runScriptsInElement(el, inlineOnly) {
    if (!el) return;
    el.querySelectorAll("script").forEach(function (oldScript) {
      // When inlineOnly is set, skip external <script src> tags (e.g. product-custom.js)
      // so they aren't fetched/executed repeatedly and don't stack duplicate listeners.
      if (inlineOnly && oldScript.src) return;

      const newScript = document.createElement("script");
      if (oldScript.src) {
        newScript.src = oldScript.src;
        if (oldScript.defer) newScript.defer = true;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      Array.from(oldScript.attributes).forEach(function (attr) {
        if (attr.name !== "src") {
          newScript.setAttribute(attr.name, attr.value);
        }
      });
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function unloadProductSectionInstance(sectionId) {
    if (!sectionId || !window.theme || !window.theme.sections) return;

    const instance = window.theme.sections._findInstance(sectionId);
    if (instance && typeof instance.onUnload === "function") {
      try {
        instance.onUnload();
      } catch (error) {
        console.warn("Kunde inte avregistrera produktsektion:", error);
      }
    }

    window.theme.sections._removeInstance(sectionId);
  }

  // Synthetic option key used to drive Impulse's image-set feature in the modal,
  // where there is no real variant object/picker to read an option value from.
  const MODAL_IMAGE_SET_KEY = "__imageSetValue";

  // Sets up Impulse's image-set (per-variant image) feature on a modal product
  // instance so the gallery only shows the current variant's media, exactly like
  // the product page.
  //
  // The quick view template (product.modal) renders NO section blocks, so the
  // variant picker and variant JSON (which the PDP uses to resolve the variant)
  // don't exist in the modal. The featured/starting slide, however, already
  // carries the current variant's image-set group in its `data-group` attribute
  // (e.g. "color_fair-00"). We read that and feed a minimal synthetic variant
  // into the instance so initProductSlider() -> imageSetArguments() rebuilds the
  // same `cellSelector: [data-group="..."]` filter the PDP uses.
  //
  // Returns the resolved image-set group, or null when the product has no image
  // sets / the group can't be resolved, in which case we fall back to showing
  // all images rather than letting updateImageSetThumbs() hide every thumbnail.
  function applyModalImageSet(section, instance) {
    if (!section || !instance || !instance.settings) return null;

    const dataSetEl = section.querySelector("[data-set-name]");
    if (!dataSetEl) {
      // Product has no tagged image sets: keep every image visible.
      instance.settings.imageSetName = null;
      instance.__modalImageSet = null;
      return null;
    }

    const setName = dataSetEl.dataset.setName;

    // The starting slide is the featured variant's media; its group is what we
    // want to filter down to. Fall back to the first grouped slide if needed.
    const groupedSlide =
      section.querySelector(".product-main-slide.starting-slide[data-group]") ||
      section.querySelector(".product-main-slide[data-group]");
    const set = groupedSlide ? groupedSlide.dataset.group : null;

    if (!setName || !set || set.indexOf(setName + "_") !== 0) {
      instance.settings.imageSetName = null;
      instance.__modalImageSet = null;
      return null;
    }

    // "color_fair-00" -> "fair-00". Impulse re-normalizes this via
    // getImageSetName(), so it round-trips back to the same group string.
    const setValue = set.slice(setName.length + 1);

    instance.settings.imageSetName = setName;
    instance.settings.imageSetIndex = MODAL_IMAGE_SET_KEY;
    instance.variants = instance.variants || {};
    instance.variants.currentVariant = {};
    instance.variants.currentVariant[MODAL_IMAGE_SET_KEY] = setValue;

    instance.__modalImageSet = set;
    return set;
  }

  // Initializes the Flickity product gallery (swipe + side arrows + thumbnail nav).
  // Inside the quick view modal Impulse's variant machinery never runs (its
  // openModalProduct bails out early because this theme renders the form inline
  // and has no [data-blocks-holder]), so the image-set feature is never wired up.
  // We resolve the selected variant ourselves and feed the image-set settings
  // into the instance, so window.initProductGallery -> instance.initProductSlider()
  // filters the gallery down to the current variant's media just like the PDP.
  function initGalleryForSection(productSection, forceReinit) {
    if (!productSection || typeof window.initProductGallery !== "function")
      return;

    if (
      productSection.dataset.modal === "true" &&
      window.theme &&
      window.theme.sections
    ) {
      const sectionId = productSection.getAttribute("data-section-id");
      const instance = window.theme.sections._findInstance(sectionId);
      if (instance && instance.settings) {
        applyModalImageSet(productSection, instance);
      }
    }

    window.initProductGallery(productSection, forceReinit);
  }

  function reinitializeProductSection(productSection) {
    if (!productSection) return;

    delete productSection.dataset.galleryArrowsInitialized;
    delete productSection.dataset.variantSyncBound;
    productSection.classList.remove("is-loading");

    const sectionId = productSection.getAttribute("data-section-id");
    const preferredColor = window.__preferredColorOnSizeSwitch;

    if (window.theme && window.theme.sections && window.theme.Product) {
      window.theme.sections._createInstance(
        productSection,
        window.theme.Product,
        productSection,
      );
    }

    if (
      preferredColor &&
      typeof window.applyPreferredColorToSection === "function"
    ) {
      window.applyPreferredColorToSection(productSection, preferredColor);
    }
    window.__preferredColorOnSizeSwitch = null;

    if (
      window.theme &&
      typeof window.theme.collapsibles !== "undefined" &&
      typeof window.theme.collapsibles.init === "function"
    ) {
      window.theme.collapsibles.init(productSection);
    }

    if (window.theme && typeof window.theme.videoModal === "function") {
      window.theme.videoModal();
    }

    if (window.lazySizes && window.lazySizes.loader) {
      window.lazySizes.loader.checkElems();
    }

    if (
      window.Shopify &&
      window.Shopify.PaymentButton &&
      typeof window.Shopify.PaymentButton.init === "function"
    ) {
      window.Shopify.PaymentButton.init();
    }

    if (window.yotpo && typeof window.yotpo.initWidgets === "function") {
      window.yotpo.initWidgets();
    }

    if (typeof window.initProductCustom === "function") {
      window.initProductCustom(productSection);
    } else if (typeof window.initProductVariantSync === "function") {
      window.initProductVariantSync(productSection);
    }

    if (typeof window.initVariantPicker === "function") {
      window.initVariantPicker(productSection);
    }

    if (window.Alpine && typeof window.Alpine.initTree === "function") {
      window.Alpine.initTree(productSection);
    }

    if (window.AOS && typeof window.AOS.refreshHard === "function") {
      window.AOS.refreshHard();
    }

    initGalleryForSection(productSection, true);

    if (typeof window.syncProductSectionVariant === "function") {
      window.syncProductSectionVariant(productSection, {
        fromSizeVersion: true,
      });
    }

    if (preferredColor && typeof window.revealColorSelection === "function") {
      window.revealColorSelection(productSection, 120);
    }

    document.dispatchEvent(
      new CustomEvent("product:section-replaced", {
        detail: { productSection: productSection, sectionId: sectionId },
      }),
    );
  }

  window._sizeVersionSwitching = false;

  document.addEventListener("click", async function (event) {
    const button = event.target.closest(
      ".variant-wrapper--size-versions .variant__button-label",
    );
    if (!button) return;

    event.preventDefault();

    if (window._sizeVersionSwitching) return;

    const productUrl = button.dataset.productUrl;
    // Resolve the section the clicked button belongs to so this also works
    // inside the quick view modal (where it is not the first one on the page).
    const currentSection =
      button.closest("[data-product-section]") ||
      document.querySelector("[data-product-section]");

    if (!productUrl || !currentSection) return;

    if (button.classList.contains("variant__button-label--current")) return;

    // Inside the quick view modal the section is rendered with data-modal="true".
    // We then load the target product's modal view so the swapped section keeps
    // the modal styling, and we must not rewrite the underlying listing URL.
    const isModalContext = currentSection.dataset.modal === "true";

    let fetchUrl = isModalContext
      ? productUrl + (productUrl.includes("?") ? "&" : "?") + "view=modal"
      : productUrl;
    fetchUrl += (fetchUrl.includes("?") ? "&" : "?") + "_=" + Date.now();

    window.__preferredColorOnSizeSwitch =
      typeof window.getSelectedColorFromSection === "function"
        ? window.getSelectedColorFromSection(currentSection)
        : null;

    let replaced = false;
    const oldSectionId = currentSection.getAttribute("data-section-id");

    try {
      window._sizeVersionSwitching = true;
      button.disabled = true;
      currentSection.classList.add("is-loading");

      const pageResponse = await fetch(fetchUrl, {
        method: "GET",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        cache: "no-store",
      });

      if (!pageResponse.ok) throw new Error("Kunde inte hämta produktsidan");

      const html = await pageResponse.text();
      const newSection = parseProductSectionFromHtml(html);

      if (!newSection) {
        throw new Error(
          "Kunde inte hitta [data-product-section] i sidans HTML",
        );
      }

      if (
        window.__preferredColorOnSizeSwitch &&
        typeof window.setColorSelectionPending === "function"
      ) {
        window.setColorSelectionPending(newSection, true);
      }

      if (typeof window.destroyProductGallery === "function") {
        window._productGalleryState = window._productGalleryState || {
          generation: 0,
          pollInterval: null,
        };
        window._productGalleryState.generation += 1;
        window.destroyProductGallery(currentSection);
      }

      unloadProductSectionInstance(oldSectionId);

      currentSection.replaceWith(newSection);
      replaced = true;

      // Only update the browser URL when switching on a real product page.
      // Inside the quick view modal we keep the underlying listing URL intact.
      if (!isModalContext) {
        window.history.replaceState({}, "", productUrl);
      }

      runScriptsInElement(newSection);
      reinitializeProductSection(newSection);
    } catch (error) {
      console.error("Kunde inte uppdatera ProductSection:", error);
    } finally {
      window._sizeVersionSwitching = false;
      button.disabled = false;

      if (!replaced) {
        window.__preferredColorOnSizeSwitch = null;
        currentSection.classList.remove("is-loading");
      }
    }
  });

  // Make the gallery interactive in the quick view modal. Impulse never runs the
  // product slider there, so swiping and the side arrows don't work out of the box.
  // The generic `modalOpen` event fires for every modal; we only act on the quick
  // shop one and initialize its gallery once the section is in place.
  document.addEventListener("modalOpen", function () {
    const modal = document.querySelector(".modal--quick-shop.modal--is-active");
    if (!modal) return;

    const section = modal.querySelector("[data-product-section]");
    if (!section) return;

    window.setTimeout(function () {
      // The product section is injected into the modal via append(), so its inline
      // <script> tags (which define window.initProductGallery) never executed.
      // Run them here (inline only) so the gallery can be initialized on first open.
      runScriptsInElement(section, true);
      initGalleryForSection(section, true);
    }, 50);
  });

  // Note: the quick view template renders no variant picker (product.modal has no
  // blocks), so there is nothing to listen to for in-modal variant switching. The
  // gallery is filtered once, to the featured variant, in initGalleryForSection().
})();
